import { useState } from "react";
import Modal from "../../components/common/Modal";
import { FormikHelpers, useFormik } from "formik";
import { ShippingCost, TShippingCostFilter } from "../../interfaces/shippingCostType";
import { usePagination } from "../../components/common/data-table/hooks/usePagination";
import { ListSearchControls } from "../../components/common/data-table/ListSearchControls";
import { ListViewControls } from "../../components/common/data-table/ListViewControls";
import Button from "../../components/common/Buttons/Button";
import { faCheck, faEdit, faXmark } from "@fortawesome/free-solid-svg-icons";
import {
  shippingCostSchema,
  useShippingCostsFilter,
  useSaveShippingCost,
  useShippingCostStatusToggle,
  useShippingCostByIdQuery,
  ShippingCostForm,
  ShippingCostTable,
  ShippingCostHeader,
} from "../../features/shippingCosts";

const formDataInit: ShippingCost = {
  name: "",
  providerName: "",
  trackingURL: "",
  cost: null,
  price: null,
  order: null,
  defaultShipping: false,
};

const filterInit: TShippingCostFilter = {};

const ShippingCostsPage = () => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingShippingCostId, setEditingShippingCostId] = useState<
    string | null
  >(null);
  const { pagination, handlePageChange, handlePageSizeChange, handleSort } =
    usePagination({
      initialPageSize: 10,
      initialSortBy: "createdAt",
      initialSortOrder: "desc",
    });

  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<TShippingCostFilter>(filterInit);

  const handleSearch = () => {
    setFilter({ ...filterInit, search: searchTerm || undefined });
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilter(filterInit);
  };

  const { shippingCosts, paginationData, isLoading } = useShippingCostsFilter({
    filter,
    pagination,
  });

  const { data: shippingCostToEdit } = useShippingCostByIdQuery(
    editingShippingCostId ?? undefined,
  );

  const { handleSave } = useSaveShippingCost();
  const { handleEnable, handleDisable } = useShippingCostStatusToggle();

  const formik = useFormik<ShippingCost>({
    initialValues: shippingCostToEdit?.data || formDataInit,
    validationSchema: shippingCostSchema,
    enableReinitialize: true,
    onSubmit: (
      values: ShippingCost,
      formikHelpers: FormikHelpers<ShippingCost>,
    ) => {
      handleSave(values);
      formikHelpers.setSubmitting(false);
      formikHelpers.resetForm({ values: formDataInit });
      handleCloseModal();
    },
  });

  const handleOpenNewShippingCostModal = () => {
    formik.resetForm({ values: formDataInit });
    setEditingShippingCostId(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    formik.resetForm({ values: formDataInit });
    setEditingShippingCostId(null);
    setShowModal(false);
  };

  const handleEditShippingCost = (shippingCost: ShippingCost) => {
    const { _id: shippingCostId } = shippingCost;
    if (!shippingCostId) return;
    setEditingShippingCostId(shippingCostId);
    setShowModal(true);
  };

  return (
    <div>
      <ShippingCostHeader />
      <ListSearchControls
        searchLabel="Buscar por nombre"
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        onSearch={handleSearch}
        onClear={handleClearSearch}
      />
      <ListViewControls
        pageSize={pagination.pageSize}
        onPageSizeChange={handlePageSizeChange}
      >
        <Button
          size="medium"
          onClick={handleOpenNewShippingCostModal}
          color="primary"
        >
          Nuevo
        </Button>
      </ListViewControls>
      <ShippingCostTable
        data={shippingCosts}
        isLoading={isLoading}
        sortBy={pagination.sortBy as keyof ShippingCost}
        sortOrder={pagination.sortOrder}
        onSort={handleSort}
        pagination={{
          currentPage: paginationData?.currentPage ?? 1,
          totalPages: paginationData?.totalPages ?? 0,
          pageSize: paginationData?.pageSize ?? 10,
          total: paginationData?.total ?? 0,
          onPageChange: handlePageChange,
        }}
        extraActions={[
          {
            title: "Editar",
            icon: faEdit,
            actionKey: "edit",
            onClick: handleEditShippingCost,
          },
          {
            title: "Deshabilitar",
            icon: faXmark,
            actionKey: "disable",
            color: "secondary",
            onClick: (row) => handleDisable(row._id!),
            shouldShow: (row) => !!row.active,
          },
          {
            title: "Habilitar",
            icon: faCheck,
            actionKey: "enable",
            color: "success",
            onClick: (row) => handleEnable(row._id!),
            shouldShow: (row) => !row.active,
          },
        ]}
      />
      <Modal show={showModal} close={handleCloseModal} submit={formik.handleSubmit}>
        <ShippingCostForm formik={formik} />
      </Modal>
    </div>
  );
};

export default ShippingCostsPage;
